import React from 'react';
import { Button, Checkbox, Modal, Typography } from 'antd';
import styled from 'styled-components';
import { ANTD_GRAY } from '../../../constants';
import { EntityAndType } from '../../../types';
import { SearchSelectActions } from './SearchSelectActions';
import { useTranslation } from '../../../../../../i18n/useTranslation';

const CheckboxContainer = styled.div`
    display: flex;
    justify-content: left;
    align-items: center;
`;

const ActionsContainer = styled.div`
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
        gap: 4px 0;
        flex-wrap: wrap;
        justify-content: space-between;
    }
`;

const CancelButton = styled(Button)`
    && {
        margin-left: 8px;
        padding: 0px;
        color: ${ANTD_GRAY[7]};
    }
`;

const StyledCheckbox = styled(Checkbox)`
    margin-right: 12px;
    padding-bottom: 0px;
`;

type Props = {
    isSelectAll: boolean;
    isIndeterminate?: boolean;
    selectedEntities?: EntityAndType[];
    showCancel?: boolean;
    showActions?: boolean;
    onChangeSelectAll: (selected: boolean) => void;
    onCancel?: () => void;
    refetch?: () => void;
};

/**
 * A header for use when an entity search select experience is active.
 *
 * This component provides a select all checkbox and a set of actions that can be taken on the selected entities.
 */
export const SearchSelectBar = ({
    isSelectAll,
    isIndeterminate = false,
    selectedEntities = [],
    showCancel = true,
    showActions = true,
    onChangeSelectAll,
    onCancel,
    refetch,
}: Props) => {
    const { t } = useTranslation();
    const selectedEntityCount = selectedEntities.length;
    const onClickCancel = () => {
        if (selectedEntityCount > 0) {
            Modal.confirm({
                centered: true,
                title: t('Exit Selection'),
                content: t('Are you sure you want to exit? {{count}} selection(s) will be cleared.', {
                    count: selectedEntityCount,
                }),
                onOk() {
                    onCancel?.();
                },
                onCancel() {},
                okText: t('Yes'),
                cancelText: t('Cancel'),
                maskClosable: true,
                closable: true,
            });
        } else {
            onCancel?.();
        }
    };

    return (
        <>
            <CheckboxContainer>
                <StyledCheckbox
                    checked={isSelectAll}
                    indeterminate={isIndeterminate}
                    onChange={(e) => onChangeSelectAll(e.target.checked as boolean)}
                />
                <Typography.Text className="f-text-medium-content f-text-single-line" type="secondary">
                    {t('{{count}} selected', { count: selectedEntityCount })}
                </Typography.Text>
            </CheckboxContainer>
            <ActionsContainer>
                {showActions && <SearchSelectActions selectedEntities={selectedEntities} refetch={refetch} />}
                {showCancel && (
                    <CancelButton onClick={onClickCancel} type="link">
                        {t('Done')}
                    </CancelButton>
                )}
            </ActionsContainer>
        </>
    );
};
